// Plant output -> taps (with line loss) -> substations -> homes. Pure and derived:
// nothing here is stored in World; tick() and the renderer both call computeDispatch.
import { homeDemandPerTick } from './demand';
import { dist } from './geometry';
import { perTick, plantOutputPerTick } from './output';
import type { Balance, Home, Id, MapData, Substation, Tap, World } from './types';

export interface TapFlow {
  sent: number;
  delivered: number;
  /** Fraction lost on this line, 0..1. */
  loss: number;
  live: boolean;
}

export interface SubstationFlow {
  /** Demand of homes assigned to this substation, capped by transformer capacity. */
  need: number;
  /** Uncapped demand of assigned homes. */
  demand: number;
  feed: number;
  served: number;
  /** Assigned home ids, nearest first. */
  homes: Id[];
}

export interface DispatchResult {
  plantOutput: Record<Id, number>;
  plantAvailable: Record<Id, number>;
  taps: Record<Id, TapFlow>;
  substations: Record<Id, SubstationFlow>;
  /** Home id -> fed this tick. Homes absent were not fed. */
  homeFed: Record<Id, boolean>;
  totalDemand: number;
  totalServed: number;
  /** Units produced by thermal plants this tick (fuel is charged on these). */
  thermalUnits: number;
}

const ROUNDS = 3;

export function lineLoss(tap: Tap, world: World, map: MapData, balance: Balance): number {
  const plant = world.plants.find((p) => p.id === tap.plantId);
  const sub = world.substations.find((s) => s.id === tap.substationId);
  if (!plant || !sub) return 1;
  return lineLossForLength(dist(plant.x, plant.y, sub.x, sub.y), tap.highVoltage, map, balance);
}

export function lineLossForLength(
  length: number,
  highVoltage: boolean,
  map: MapData,
  balance: Balance,
): number {
  const per20 = balance.line.lossPer20PctMapWidth;
  let loss = (length / (0.2 * map.size.w)) * per20;
  if (highVoltage) loss *= 1 - balance.line.highVoltageLossReduction;
  return Math.min(0.95, Math.max(0, loss));
}

export function isTapLive(tap: Tap, world: World): boolean {
  if (tap.faultUntilTick !== undefined && world.time.tick < tap.faultUntilTick) return false;
  const plant = world.plants.find((p) => p.id === tap.plantId);
  if (!plant) return false;
  return world.time.day >= plant.onlineDay;
}

/** Nearest substation whose radius covers the home, or undefined. */
export function substationForHome(home: Home, substations: Substation[]): Substation | undefined {
  let best: Substation | undefined;
  let bestD = Infinity;
  for (const s of substations) {
    const d = dist(home.x, home.y, s.x, s.y);
    if (d <= s.radius && d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}

export function computeDispatch(world: World, map: MapData, balance: Balance): DispatchResult {
  const demandEach = homeDemandPerTick(world.time.hour, balance);
  const result: DispatchResult = {
    plantOutput: {},
    plantAvailable: {},
    taps: {},
    substations: {},
    homeFed: {},
    totalDemand: 0,
    totalServed: 0,
    thermalUnits: 0,
  };

  // Assign live homes to substations.
  const assigned: Record<Id, { home: Home; d: number }[]> = {};
  for (const s of world.substations) {
    assigned[s.id] = [];
    result.substations[s.id] = { need: 0, demand: 0, feed: 0, served: 0, homes: [] };
  }
  for (const home of world.homes) {
    if (home.status === 'dark') continue;
    result.totalDemand += demandEach;
    const s = substationForHome(home, world.substations);
    if (!s) continue;
    assigned[s.id]?.push({ home, d: dist(home.x, home.y, s.x, s.y) });
  }
  const remainingNeed: Record<Id, number> = {};
  for (const s of world.substations) {
    const list = assigned[s.id] ?? [];
    list.sort((a, b) => a.d - b.d || (a.home.id < b.home.id ? -1 : 1));
    const flow = result.substations[s.id];
    if (!flow) continue;
    flow.homes = list.map((e) => e.home.id);
    flow.demand = list.length * demandEach;
    flow.need = Math.min(flow.demand, perTick(s.capacity, balance));
    remainingNeed[s.id] = flow.need;
  }

  // Plant output and live taps.
  const remainingOut: Record<Id, number> = {};
  for (const p of world.plants) {
    const out = plantOutputPerTick(p, world, map, balance);
    result.plantAvailable[p.id] = out;
    result.plantOutput[p.id] = 0;
    remainingOut[p.id] = out;
  }
  for (const t of world.taps) {
    result.taps[t.id] = {
      sent: 0,
      delivered: 0,
      loss: lineLoss(t, world, map, balance),
      live: isTapLive(t, world),
    };
  }

  // Water-filling: each round, every plant splits what it has left across its live taps
  // in proportion to the remaining need of the substations at the far end.
  for (let round = 0; round < ROUNDS; round++) {
    for (const p of world.plants) {
      const out = remainingOut[p.id] ?? 0;
      if (out <= 1e-12) continue;
      const taps = world.taps.filter((t) => t.plantId === p.id && result.taps[t.id]?.live);
      let totalNeed = 0;
      for (const t of taps) totalNeed += remainingNeed[t.substationId] ?? 0;
      if (totalNeed <= 1e-12) continue;
      let used = 0;
      for (const t of taps) {
        const need = remainingNeed[t.substationId] ?? 0;
        if (need <= 1e-12) continue;
        const flow = result.taps[t.id];
        if (!flow) continue;
        const efficiency = 1 - flow.loss;
        const share = (out * need) / totalNeed;
        const send = Math.min(share, need / efficiency);
        const delivered = send * efficiency;
        flow.sent += send;
        flow.delivered += delivered;
        used += send;
        remainingNeed[t.substationId] = need - delivered;
        const sub = result.substations[t.substationId];
        if (sub) sub.feed += delivered;
      }
      remainingOut[p.id] = out - used;
    }
  }
  for (const p of world.plants) {
    const produced = (result.plantAvailable[p.id] ?? 0) - (remainingOut[p.id] ?? 0);
    result.plantOutput[p.id] = produced;
    if (p.type === 'thermal') result.thermalUnits += produced;
  }

  // Serve homes nearest-first until the feed runs out.
  for (const s of world.substations) {
    const flow = result.substations[s.id];
    if (!flow) continue;
    let available = flow.feed;
    for (const id of flow.homes) {
      if (available + 1e-9 >= demandEach) {
        available -= demandEach;
        flow.served += demandEach;
        result.homeFed[id] = true;
      }
    }
    result.totalServed += flow.served;
  }

  return result;
}
